import { apiClient } from '@/http/api-client';
import { ApiDataWrapper } from '@/http/types';
import { z } from 'zod';

export type SsoProvider = 'google';

interface LoginResponse {
  token: string;
}

interface SsoRequestData {
  redirect_url: string;
}

interface SsoLoginData {
  access_token: string;
  expires_at: string;
}

const adminMeSchema = z.object({
  data: z.object({
    user_id: z.string().min(1),
    email: z.email(),
    name: z.string().nullable(),
    status: z.string(),
    group: z.string(),
    roles: z.array(z.string()),
  }),
});

export class AdminAccountRequiredError extends Error {
  constructor() {
    super('An active platform admin account is required');
    this.name = 'AdminAccountRequiredError';
  }
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  status: string;
  group: string;
  permissions: string[];
  companyId: string | null;
  employeeId: string | null;
  avatar: string | null;
  isSso?: boolean;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export const authService = {
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    const response = await apiClient.post<ApiDataWrapper<SsoLoginData>>(
      '/v1/im/auth/admin/login',
      credentials
    );
    return { token: response.data.access_token };
  },

  getSsoRedirectUrl: async (provider: SsoProvider): Promise<string> => {
    const callbackUrl = `${window.location.origin}/oauth/code/google`;
    const response = await apiClient.get<ApiDataWrapper<SsoRequestData>>(
      `/v1/im/auth/admin/oauth/${provider}/request?redirect_uri=${encodeURIComponent(callbackUrl)}`
    );
    return response.data.redirect_url;
  },

  handleSsoCallback: async (provider: SsoProvider, code: string): Promise<LoginResponse> => {
    const callbackUrl = `${window.location.origin}/oauth/code/${provider}`;
    const response = await apiClient.post<ApiDataWrapper<SsoLoginData>>(
      `/v1/im/auth/admin/oauth/${provider}/login?redirect_uri=${encodeURIComponent(callbackUrl)}`,
      { code }
    );
    return { token: response.data.access_token };
  },

  getMe: async (): Promise<AuthUser> => {
    const response = await apiClient.get('/v1/im/admin/me', adminMeSchema);
    const data = response.data;
    if (data.status !== 'ACTIVE' || data.group !== 'XHR_ADMIN') {
      throw new AdminAccountRequiredError();
    }
    return {
      id: data.user_id,
      email: data.email,
      name: data.name === null ? data.email : data.name,
      status: data.status,
      group: data.group,
      permissions: [],
      companyId: null,
      employeeId: null,
      avatar: null,
    };
  },
};
